import React, { useContext } from 'react';
import { Select, InputNumber, Button, Space, Typography, Card, Row, Col, Tag } from 'antd';
import { PlusOutlined, MinusOutlined, GiftOutlined } from '@ant-design/icons';
import { DarkModeContext } from '../../contexts/darkModeContext';
import './searchDropDown.scss';

const { Text } = Typography;
const { Option } = Select;

const MAX_ITEMS = 100;

const SearchDropDown = ({ dataArr, items, setItems, hasQuantity }) => {
    const { darkMode } = useContext(DarkModeContext);

    const handleAddItem = () => {
        if (items.length < MAX_ITEMS) {
            setItems([...items, { id: 101, type: 1, quantity: 0 }]);
        }
    };

    const handleRemoveItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const handleItemChange = (index, value) => {
        if (value) {
            const selectedItem = dataArr.find(item => item.key === value);
            if (selectedItem) {
                const newItems = [...items];
                newItems[index].id = selectedItem.index;
                newItems[index].type = selectedItem.itemType;
                setItems(newItems);
            }
        }
    };

    const handleItemQuantityChange = (index, value) => {
        const newItems = [...items];
        newItems[index].quantity = Number(value) || 0;
        setItems(newItems);
    };

    return (
        <div className={`search-dropdown-container ${darkMode ? 'dark-theme' : 'light-theme'}`}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {items.map((item, index) => {
                    const selectedItem = dataArr.find(
                        (o) => o.index === item.id && o.itemType === item.type
                    );

                    return (
                        <Card
                            key={index}
                            size="small"
                            className="item-card"
                            styles={{
                                body: {
                                    padding: '16px'
                                }
                            }}
                        >
                            <Row gutter={[12, 12]} align="middle">
                                {/* 아이템 선택 */}
                                <Col xs={24} sm={hasQuantity ? 14 : 18}>
                                    <div className="item-select-container">
                                        <Text strong className="field-label">
                                            <GiftOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                                            아이템 선택
                                        </Text>
                                        <Select
                                            className="item-select"
                                            value={selectedItem?.key}
                                            onChange={(value) => handleItemChange(index, value)}
                                            placeholder="아이템을 선택하세요"
                                            size="large"
                                            showSearch
                                            filterOption={(input, option) => {
                                                const item = dataArr.find(d => d.key === option.value);
                                                return item?.name?.toLowerCase().includes(input.toLowerCase()) ||
                                                    item?.index?.toString().includes(input);
                                            }}
                                            style={{ width: '100%' }}
                                            dropdownStyle={{ maxHeight: 400 }}
                                            optionLabelProp="label"
                                        >
                                            {dataArr.map((option) => (
                                                <Option
                                                    key={option.key}
                                                    value={option.key}
                                                    label={option.name}
                                                >
                                                    <div className="item-option">
                                                        <div className="item-name">
                                                            <Text strong>{option.name}</Text>
                                                        </div>
                                                        <div className="item-details">
                                                            <Tag size="small" color="blue">
                                                                ID: {option.index}
                                                            </Tag>
                                                            <Tag size="small" color="green">
                                                                Type: {option.itemType}
                                                            </Tag>
                                                        </div>
                                                    </div>
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>
                                </Col>

                                {/* 수량 입력 (hasQuantity가 true인 경우) */}
                                {hasQuantity && (
                                    <Col xs={24} sm={6}>
                                        <div className="quantity-container">
                                            <Text strong className="field-label">
                                                수량
                                            </Text>
                                            <InputNumber
                                                className="quantity-input"
                                                value={item.quantity}
                                                onChange={(value) => handleItemQuantityChange(index, value)}
                                                min={0}
                                                max={999999999}
                                                size="large"
                                                style={{ width: '100%' }}
                                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                                placeholder="수량"
                                            />
                                        </div>
                                    </Col>
                                )}

                                {/* 삭제 버튼 */}
                                <Col xs={24} sm={hasQuantity ? 4 : 6}>
                                    <div className="remove-container">
                                        <Text strong className="field-label" style={{ visibility: 'hidden' }}>
                                            삭제
                                        </Text>
                                        <Button
                                            type="text"
                                            danger
                                            icon={<MinusOutlined />}
                                            onClick={() => handleRemoveItem(index)}
                                            size="large"
                                            className="remove-button"
                                            style={{ width: '100%' }}
                                        >
                                            삭제
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {/* 선택된 아이템 정보 표시 */}
                            {selectedItem && (
                                <div className="selected-item-info">
                                    <Space wrap>
                                        <Tag color="blue">
                                            <Text strong>{selectedItem.name}</Text>
                                        </Tag>
                                        <Tag color="green">
                                            ID: {selectedItem.index}
                                        </Tag>
                                        <Tag color="purple">
                                            Type: {selectedItem.itemType}
                                        </Tag>
                                        {hasQuantity && item.quantity > 0 && (
                                            <Tag color="orange">
                                                수량: {item.quantity.toLocaleString()}
                                            </Tag>
                                        )}
                                    </Space>
                                </div>
                            )}
                        </Card>
                    );
                })}

                {/* 아이템 추가 버튼 */}
                {items.length < MAX_ITEMS && (
                    <Card size="small" className="add-item-card">
                        <div style={{ textAlign: 'center', padding: '12px' }}>
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={handleAddItem}
                                size="large"
                                className="add-button"
                                style={{ width: '100%', height: '48px' }}
                            >
                                아이템 추가 ({items.length}/{MAX_ITEMS})
                            </Button>
                        </div>
                    </Card>
                )}
            </Space>
        </div>
    );
};

export default SearchDropDown;